import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithChildren,
  PropsWithoutRef,
  RefAttributes,
} from 'react';
import MuiTypography from '@mui/material/Typography';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type BaseVariant = 'primary' | 'secondary' | 'system';
type TitleLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type CommonProps = {
  strong?: boolean;
  siloreal?: boolean;
} & PropsWithChildren;

type TitleProps = CommonProps & {
  variant: 'title';
  level?: TitleLevel;
};

type BodyProps = CommonProps & {
  variant: BaseVariant;
  level?: never;
};

export type Props = TitleProps | BodyProps;

const TITLE_FONT_SIZE_BY_LEVEL: Record<TitleLevel, number> = {
  h1: 36,
  h2: 32,
  h3: 28,
  h4: 24,
  h5: 20,
  h6: 18,
};

export const Typography: FR<HTMLElement, Props> = forwardRef((props: Props, ref) => {
  const { children, variant, strong, siloreal } = props;

  const silorealSx = siloreal
    ? {
        background: 'linear-gradient(90deg, #6baa00 0%, #00c8ff 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
        display: 'inline-block',
        width: 'fit-content',
      }
    : undefined;

  if (variant === 'title') {
    const level = props.level ?? 'h2';

    return (
      <MuiTypography
        ref={ref}
        fontSize={TITLE_FONT_SIZE_BY_LEVEL[level]}
        fontWeight={strong ? 900 : 800}
        sx={silorealSx}
      >
        {children}
      </MuiTypography>
    );
  }

  if (variant === 'secondary') {
    return (
      <MuiTypography
        ref={ref}
        fontSize={12}
        color="textSecondary"
        fontWeight={strong ? 700 : 400}
        sx={silorealSx}
      >
        {children}
      </MuiTypography>
    );
  }

  if (variant === 'system') {
    return (
      <MuiTypography
        ref={ref}
        fontSize={11}
        color="textSecondary"
        fontStyle="italic"
        fontWeight={strong ? 700 : 400}
        sx={silorealSx}
      >
        {children}
      </MuiTypography>
    );
  }

  return (
    <MuiTypography ref={ref} fontSize={14} fontWeight={strong ? 700 : 400} sx={silorealSx}>
      {children}
    </MuiTypography>
  );
});
